/**
 * Orders history on the patient profile (orders linked to this patient file).
 */
import { useState } from 'react';
import { Box, Card, CardContent, Divider, Rating, Typography } from '@mui/material';
import { ReceiptLong as OrdersIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { DataTable, StatusChip, type Column } from '../common';
import { useGetOrdersQuery } from '../../store/api/ordersApi';
import type { OrderListItem } from '../../types/order';

const PatientOrdersSection = ({ patientId }: { patientId: number }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);

  const { data, isLoading, isFetching } = useGetOrdersQuery({ patient_id: patientId, page: page + 1, page_size: 10 });
  const orders = data?.results ?? [];

  const columns: Column<OrderListItem>[] = [
    { id: 'order_number', label: t('users.orders.orderNumber') },
    {
      id: 'service',
      label: t('users.orders.service'),
      render: (row) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {row.subservice_name || row.service_name}
          </Typography>
          {row.subservice_name && (
            <Typography variant="caption" color="text.secondary">
              {row.service_name}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      id: 'scheduled_date',
      label: t('users.orders.scheduled'),
      render: (row) => (
        <Typography variant="body2">
          {moment(row.scheduled_date).format('DD MMM YYYY')}
          {row.scheduled_time_from ? ` · ${row.scheduled_time_from.slice(0, 5)}` : ''}
        </Typography>
      ),
    },
    {
      id: 'assigned_provider',
      label: t('users.orders.provider'),
      render: (row) => row.assigned_provider?.name || '—',
    },
    { id: 'total_amount', label: t('users.orders.amount'), render: (row) => `EGP ${row.total_amount}` },
    { id: 'status', label: t('users.orders.status'), render: (row) => <StatusChip status={row.status} /> },
    {
      id: 'service_rating',
      label: t('users.orders.rating'),
      render: (row) => (row.service_rating ? <Rating value={row.service_rating} readOnly size="small" /> : '—'),
    },
    {
      id: 'created_at',
      label: t('users.orders.requestedAt'),
      render: (row) => moment(row.created_at).format('DD MMM YYYY HH:mm'),
    },
  ];

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <OrdersIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight={600}>
            {t('users.sections.orders')}
            {data?.count ? ` (${data.count})` : ''}
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <DataTable
          columns={columns}
          data={orders}
          loading={isLoading || isFetching}
          page={page}
          rowsPerPage={10}
          totalCount={data?.count ?? 0}
          onPageChange={setPage}
          emptyMessage={t('users.orders.empty')}
          onRowClick={(row) => navigate(`/orders/${row.id}`)}
        />
      </CardContent>
    </Card>
  );
};

export default PatientOrdersSection;
